import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket, faBolt, faLock, faBookOpen, faKey, faCode, faBell,
  faCopy, faExclamationTriangle, faPencilAlt, faCheckCircle, faChartBar,
  faClock, faTimesCircle, faBan, faSearch, faLightbulb, faCogs,
  faMoneyBillTransfer, faClipboardList, faWallet, faBuildingColumns,
  faBars, faStar, faArrowRight, faShieldHalved, faGlobe,
  faUserPlus, faMobileAlt, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { faJsSquare, faPhp, faPython } from '@fortawesome/free-brands-svg-icons';

/* ─────────────────────────────────────────────────────────────────
   STYLES — Light / Warm-Cream theme
───────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,400;0,500;0,600;0,700;1,400&family=IBM+Plex+Sans:wght@400;500;600;700;800&display=swap');

  .pdoc-root * { box-sizing: border-box; }
  .pdoc-root {
    font-family: 'IBM Plex Sans', system-ui, sans-serif;
    background: #f7f3eb;
    color: #1c1510;
  }

  @keyframes pdoc-shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
  @keyframes pdoc-pulse-dot {
    0%,100% { opacity:1;   box-shadow:0 0 5px #15803d; }
    50%      { opacity:0.5; box-shadow:0 0 2px #15803d; }
  }
  @keyframes pdoc-fade-up {
    from { opacity:0; transform:translateY(10px); }
    to   { opacity:1; transform:translateY(0);    }
  }
  @keyframes pdoc-glow {
    0%,100% { box-shadow:0 0 10px rgba(181,101,10,0.18); }
    50%     { box-shadow:0 0 22px rgba(181,101,10,0.38); }
  }
  @keyframes pdoc-grid-breathe {
    0%,100% { opacity:0.5; }
    50%     { opacity:0.85; }
  }

  .pdoc-hero-grid {
    background-image:
      linear-gradient(rgba(181,101,10,0.10) 1px, transparent 1px),
      linear-gradient(90deg, rgba(181,101,10,0.10) 1px, transparent 1px);
    background-size: 48px 48px;
    animation: pdoc-grid-breathe 7s ease-in-out infinite;
  }
  .pdoc-status-dot  { animation: pdoc-pulse-dot 2.5s ease-in-out infinite; }
  .pdoc-content-in  { animation: pdoc-fade-up   0.35s ease-out; }
  .pdoc-cta-glow    { animation: pdoc-glow       3s ease-in-out infinite; }

  /* Sidebar nav links */
  .pdoc-sidebar-link {
    position: relative;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.75rem 0.45rem 1rem;
    border-radius: 0.375rem;
    font-size: 0.8rem;
    font-weight: 500;
    color: #8a7a68;
    transition: color 0.12s, background 0.12s, padding-left 0.12s;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    font-family: 'IBM Plex Sans', sans-serif;
  }
  .pdoc-sidebar-link:hover {
    color: #3d2e1e;
    background: rgba(181,101,10,0.06);
    padding-left: 1.25rem;
  }
  .pdoc-sidebar-link.pdoc-active {
    color: #9a5000;
    background: rgba(181,101,10,0.10);
    padding-left: 1.25rem;
    font-weight: 600;
  }
  .pdoc-sidebar-link.pdoc-active::before {
    content: '';
    position: absolute;
    left: 0; top: 6px; bottom: 6px;
    width: 2.5px;
    background: linear-gradient(to bottom, #b5650a, #d4870a);
    border-radius: 0 2px 2px 0;
    box-shadow: 0 0 6px rgba(181,101,10,0.4);
  }

  /* Code blocks — dark panel on light page */
  .pdoc-code-wrap {
    position: relative;
    background: #1c1812;
    border: 1px solid #2e2a22;
    border-radius: 0.625rem;
    overflow: hidden;
    margin-bottom: 1rem;
    font-family: 'IBM Plex Mono','Cascadia Code','Fira Code',monospace;
    box-shadow: 0 2px 12px rgba(0,0,0,0.10);
  }
  .pdoc-code-wrap::after {
    content: '';
    position: absolute;
    top:0; left:0; right:0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #b5650a 35%, #d4870a 50%, #b5650a 65%, transparent);
    background-size: 200% 100%;
    animation: pdoc-shimmer 4.5s ease-in-out infinite;
  }

  .pdoc-method {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 0.63rem; font-weight: 700;
    letter-spacing: 0.1em;
    padding: 0.2rem 0.58rem;
    border-radius: 3px;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  .pdoc-ep-card {
    background: #ffffff;
    border: 1px solid #dfd5c5;
    border-radius: 0.75rem;
    overflow: hidden;
    margin-bottom: 1.5rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .pdoc-ep-card:hover {
    border-color: #c8b8a0;
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
  }

  .pdoc-field-code {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 0.7rem;
    background: rgba(181,101,10,0.08);
    color: #9a5000;
    padding: 0.15rem 0.4rem;
    border-radius: 3px;
    border: 1px solid rgba(181,101,10,0.18);
    white-space: nowrap;
  }

  .pdoc-table { font-size:0.8rem; width:100%; border-collapse:collapse; }
  .pdoc-table th {
    text-align: left;
    padding: 0.6rem 1rem;
    font-size: 0.65rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.09em;
    color: #8a7a68;
    border-bottom: 1px solid #dfd5c5;
    background: #f7f2ea;
    font-family: 'IBM Plex Mono', monospace;
  }
  .pdoc-table td {
    padding: 0.6rem 1rem;
    border-bottom: 1px solid #ede8de;
    color: #3a3028;
    vertical-align: top;
    line-height: 1.5;
  }
  .pdoc-table tr:last-child td { border-bottom: none; }
  .pdoc-table tbody tr:hover td { background: rgba(181,101,10,0.025); }

  .pdoc-info {
    border-radius: 0.5rem;
    padding: 0.875rem 1rem;
    display: flex; gap: 0.75rem;
    align-items: flex-start;
    margin-bottom: 1.25rem;
    font-size: 0.8rem; line-height: 1.65;
    border-left: 3px solid;
  }

  .pdoc-section-hdr {
    display: flex; align-items: center; gap: 0.75rem;
    font-size: 1.1rem; font-weight: 700;
    color: #1c1510;
    margin-bottom: 1.25rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e8dfd2;
    font-family: 'IBM Plex Sans', sans-serif;
  }
  .pdoc-section-icon {
    width:2rem; height:2rem; border-radius:0.375rem;
    display:flex; align-items:center; justify-content:center;
    background: rgba(181,101,10,0.10); color:#b5650a;
    font-size:0.85rem; flex-shrink:0;
  }

  .pdoc-copy-btn {
    display:flex; align-items:center; gap:0.3rem;
    background:none; border:none; cursor:pointer;
    font-family:'IBM Plex Mono',monospace;
    font-size:0.68rem; color:#5a4c38;
    transition:color 0.15s; padding:0;
  }
  .pdoc-copy-btn:hover { color:#d4870a; }

  .pdoc-lang-btn {
    display:flex; align-items:center; gap:0.375rem;
    padding:0.4rem 0.875rem;
    border-radius:0.375rem;
    border:1px solid #dfd5c5;
    background:#ffffff;
    font-family:'IBM Plex Sans',sans-serif;
    font-size:0.78rem; font-weight:500;
    cursor:pointer; transition:all 0.15s; color:#6a5a48;
  }
  .pdoc-lang-btn:hover { border-color:#c8b8a0; color:#3d2e1e; background:#faf5ee; }
  .pdoc-lang-btn.pdoc-lang-active {
    background:rgba(181,101,10,0.08);
    border-color:rgba(181,101,10,0.35);
    color:#9a5000; font-weight:600;
  }

  .pdoc-ver-btn {
    flex:1; padding:0.35rem 0;
    border:none; border-radius:3px; cursor:pointer;
    font-family:'IBM Plex Mono',monospace;
    font-size:0.68rem; font-weight:700;
    transition:all 0.15s;
  }

  .pdoc-search-input:focus {
    outline:none;
    border-color:rgba(181,101,10,0.45) !important;
    box-shadow:0 0 0 3px rgba(181,101,10,0.10);
  }

  @media (max-width:1023px) {
    .pdoc-sidebar {
      position:fixed !important;
      left:0; top:0; bottom:0; z-index:40;
      transform:translateX(-100%);
      transition:transform 0.3s ease;
      width:260px !important; height:100vh !important;
    }
    .pdoc-sidebar.pdoc-open {
      transform:translateX(0);
      box-shadow:4px 0 24px rgba(0,0,0,0.15);
    }
    .pdoc-fab  { display:flex !important; }
    .pdoc-main { padding:1.25rem 1rem !important; }
  }
`;

/* ─────────────────────────────────────────────────────────────────
   NAV CONFIG
───────────────────────────────────────────────────────────────── */
interface NavTab { id:string; icon:any; label:string; category:string; }

const NAV_TABS: NavTab[] = [
  { id:'overview',       icon:faBookOpen,            label:'Overview',        category:'Getting Started' },
  { id:'authentication', icon:faKey,                 label:'Authentication',  category:'Getting Started' },
  { id:'orders',         icon:faRocket,              label:'Orders API',      category:'API Reference'   },
  { id:'slip-order',     icon:faClipboardList,       label:'Slip Order API',  category:'API Reference'   },
  { id:'payout',         icon:faMoneyBillTransfer,   label:'Payout API',      category:'API Reference'   },
  { id:'balance',        icon:faWallet,              label:'Balance API',     category:'API Reference'   },
  { id:'banks',          icon:faBuildingColumns,     label:'Banks API',       category:'API Reference'   },
  { id:'system-status',  icon:faCogs,                label:'System Status',   category:'API Reference'   },
  { id:'webhook',        icon:faBell,                label:'Webhooks',        category:'Advanced'        },
  { id:'errors',         icon:faExclamationTriangle, label:'Error Codes',     category:'Advanced'        },
  { id:'examples',       icon:faCode,                label:'Code Examples',   category:'Advanced'        },
];
const NAV_CATS = ['Getting Started','API Reference','Advanced'];

/* ─────────────────────────────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────────────────────────────── */
interface CBProps {
  code:string; label?:string; id?:string; noCopy?:boolean;
  copiedStates:Record<string,boolean>;
  onCopy:(code:string,id?:string)=>void;
}
const CodeBlock = React.memo<CBProps>(({ code, label, id, noCopy, copiedStates, onCopy }) => (
  <div className="pdoc-code-wrap">
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.45rem 1rem', background:'#151109', borderBottom:'1px solid #2e2a22' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'0.375rem' }}>
        {[0,1,2].map(i => <span key={i} style={{ width:8, height:8, borderRadius:'50%', background:'#4a3c28', display:'block' }} />)}
        {label && <span style={{ color:'#5a4c38', fontFamily:'IBM Plex Mono,monospace', fontSize:'0.68rem', marginLeft:'0.375rem' }}>{label}</span>}
      </div>
      {!noCopy && (
        <button className="pdoc-copy-btn" style={{ color: copiedStates[id||''] ? '#15803d' : '#5a4c38' }} onClick={() => onCopy(code,id)}>
          <FontAwesomeIcon icon={copiedStates[id||''] ? faCheckCircle : faCopy} style={{ fontSize:'0.65rem' }} />
          {copiedStates[id||''] ? 'Copied!' : 'Copy'}
        </button>
      )}
    </div>
    <pre style={{ margin:0, padding:'1.1rem 1.25rem', overflowX:'auto', fontSize:'0.76rem', lineHeight:1.75, color:'#d4a868', whiteSpace:'pre', tabSize:2 }}>{code}</pre>
  </div>
));

const MethodBadge = React.memo<{ method:string }>(({ method }) => {
  const S: Record<string,React.CSSProperties> = {
    GET:    { background:'rgba(2,132,199,0.10)',  color:'#0369a1', border:'1px solid rgba(2,132,199,0.25)'  },
    POST:   { background:'rgba(21,128,61,0.10)',  color:'#15803d', border:'1px solid rgba(21,128,61,0.25)'  },
    PUT:    { background:'rgba(181,101,10,0.10)', color:'#9a5000', border:'1px solid rgba(181,101,10,0.25)' },
    DELETE: { background:'rgba(220,38,38,0.10)',  color:'#b91c1c', border:'1px solid rgba(220,38,38,0.25)'  },
  };
  return <span className="pdoc-method" style={S[method]||S.GET}>{method}</span>;
});

interface EPCardProps {
  method: string; path: string; children: React.ReactNode;
  curl?: string; curlId?: string;
  cb?: { copiedStates: Record<string,boolean>; onCopy: (code:string, id?:string) => void };
}
const EndpointCard: React.FC<EPCardProps> = ({ method, path, curl, curlId, cb, children }) => (
  <div className="pdoc-ep-card">
    <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.7rem 1.25rem', background:'#faf5ee', borderBottom:'1px solid #dfd5c5' }}>
      <MethodBadge method={method} />
      <code style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:'0.8rem', color:'#3d2e1e', fontWeight:500 }}>{path}</code>
    </div>
    <div style={{ padding:'1.25rem' }}>
      {children}
      {curl && cb && <CurlBox id={curlId || `curl-${method}-${path}`} curl={curl} {...cb} />}
    </div>
  </div>
);

const SectionTitle: React.FC<{ icon:any; children:React.ReactNode }> = ({ icon, children }) => (
  <div className="pdoc-section-hdr">
    <span className="pdoc-section-icon"><FontAwesomeIcon icon={icon} /></span>
    {children}
  </div>
);

type InfoType = 'info'|'warning'|'success'|'tip';
const INFO_CFG: Record<InfoType,{ bg:string; border:string; icon:any; ic:string; tc:string }> = {
  info:    { bg:'rgba(2,132,199,0.06)',  border:'#0ea5e9', icon:faLightbulb,          ic:'#0284c7', tc:'#075985' },
  warning: { bg:'rgba(181,101,10,0.07)', border:'#b5650a', icon:faExclamationTriangle, ic:'#9a5000', tc:'#7a3d00' },
  success: { bg:'rgba(21,128,61,0.07)',  border:'#16a34a', icon:faCheckCircle,         ic:'#15803d', tc:'#14532d' },
  tip:     { bg:'rgba(124,58,237,0.06)', border:'#7c3aed', icon:faLightbulb,           ic:'#7c3aed', tc:'#4c1d95' },
};
const InfoCard: React.FC<{ type:InfoType; title:string; children:React.ReactNode }> = ({ type, title, children }) => {
  const c = INFO_CFG[type];
  return (
    <div className="pdoc-info" style={{ background:c.bg, borderColor:c.border }}>
      <FontAwesomeIcon icon={c.icon} style={{ color:c.ic, marginTop:2, flexShrink:0 }} />
      <div>
        <strong style={{ color:c.ic, fontSize:'0.78rem', display:'block', marginBottom:'0.15rem' }}>{title}</strong>
        <span style={{ color:c.tc }}>{children}</span>
      </div>
    </div>
  );
};

const ApiTable = React.memo<{ headers:string[]; rows:React.ReactNode[][] }>(({ headers, rows }) => (
  <div style={{ overflowX:'auto', marginBottom:'1.25rem', borderRadius:'0.5rem', border:'1px solid #dfd5c5', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
    <table className="pdoc-table">
      <thead><tr>{headers.map((h,i) => <th key={i}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((row,i) => <tr key={i}>{row.map((cell,j) => <td key={j}>{cell}</td>)}</tr>)}</tbody>
    </table>
  </div>
));

const FC: React.FC<{ children:string }> = ({ children }) => <code className="pdoc-field-code">{children}</code>;
const Req: React.FC = () => (
  <span style={{ padding:'0.15rem 0.5rem', background:'rgba(220,38,38,0.08)', color:'#b91c1c', borderRadius:3, fontSize:'0.63rem', fontWeight:700, fontFamily:'IBM Plex Mono,monospace', letterSpacing:'0.06em' }}>REQUIRED</span>
);
const Opt: React.FC = () => (
  <span style={{ padding:'0.15rem 0.5rem', background:'rgba(100,90,80,0.08)', color:'#8a7a68', borderRadius:3, fontSize:'0.63rem', fontWeight:600, fontFamily:'IBM Plex Mono,monospace', letterSpacing:'0.06em' }}>OPTIONAL</span>
);
const SBadge: React.FC<{ bg:string; color:string; icon:any; children:string }> = ({ bg, color, icon, children }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:'0.375rem', padding:'0.3rem 0.75rem', borderRadius:4, background:bg, color, fontSize:'0.73rem', fontWeight:600, fontFamily:'IBM Plex Mono,monospace', marginRight:'0.5rem', marginBottom:'0.5rem' }}>
    <FontAwesomeIcon icon={icon} style={{ fontSize:'0.6rem' }} />{children}
  </span>
);

interface CurlBoxProps {
  curl: string; id: string;
  copiedStates: Record<string,boolean>;
  onCopy: (code:string, id?:string) => void;
}
const CurlBox = React.memo<CurlBoxProps>(({ curl, id, copiedStates, onCopy }) => (
  <div style={{ marginTop:'1.25rem', paddingTop:'1.125rem', borderTop:'1px dashed #e8dfd2' }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.5rem' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
        <span style={{ fontSize:'0.6rem', fontFamily:'IBM Plex Mono,monospace', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', background:'rgba(181,101,10,0.10)', border:'1px solid rgba(181,101,10,0.22)', color:'#9a5000', padding:'0.15rem 0.5rem', borderRadius:3 }}>
          cURL
        </span>
        <span style={{ fontSize:'0.68rem', color:'#a89880' }}>copy &amp; import into Postman</span>
      </div>
    </div>
    <div className="pdoc-code-wrap" style={{ marginBottom:0 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.4rem 1rem', background:'#151109', borderBottom:'1px solid #2e2a22' }}>
        <span style={{ color:'#5a4c38', fontFamily:'IBM Plex Mono,monospace', fontSize:'0.65rem' }}>terminal / postman</span>
        <button className="pdoc-copy-btn" style={{ color: copiedStates[id] ? '#15803d' : '#5a4c38' }} onClick={() => onCopy(curl, id)}>
          <FontAwesomeIcon icon={copiedStates[id] ? faCheckCircle : faCopy} style={{ fontSize:'0.65rem' }} />
          {copiedStates[id] ? 'Copied!' : 'Copy cURL'}
        </button>
      </div>
      <pre style={{ margin:0, padding:'0.875rem 1.25rem', overflowX:'auto', fontSize:'0.73rem', lineHeight:1.75, color:'#d4a868', whiteSpace:'pre', fontFamily:'IBM Plex Mono,monospace' }}>{curl}</pre>
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */
const PublicApiDocsPage: React.FC = () => {
  const [activeTab,      setActiveTab]      = useState('overview');
  const [apiVersion,     setApiVersion]     = useState<'v1'|'v2'>('v2');
  const [selectedLang,   setSelectedLang]   = useState<'javascript'|'php'|'python'>('javascript');
  const [mobileNavOpen,  setMobileNavOpen]  = useState(false);
  const [copiedStates,   setCopiedStates]   = useState<Record<string,boolean>>({});
  const [sidebarSearch,  setSidebarSearch]  = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  const getBaseUrl = (v: 'v1'|'v2' = 'v2') => {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return `http://localhost:5000/api/${v}`;
    const parts = host.split('.');
    const base  = parts.length >= 2 ? parts.slice(-2).join('.') : host;
    return `${window.location.protocol}//api.${base}/api/${v}`;
  };

  const baseUrl   = getBaseUrl(apiVersion);
  const baseUrlV2 = getBaseUrl('v2');
  const PK = 'pk_your_api_key';

  useEffect(() => {
    contentRef.current?.scrollTo({ top:0, behavior:'smooth' });
  }, [activeTab]);

  const copyCode = async (code:string, id?:string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement('textarea');
        ta.value = code;
        Object.assign(ta.style, { position:'fixed', left:'-9999px', top:'-9999px' });
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      if (id) {
        setCopiedStates(p => ({ ...p, [id]:true }));
        setTimeout(() => setCopiedStates(p => ({ ...p, [id]:false })), 2000);
      }
    } catch {}
  };

  const CB = { copiedStates, onCopy: copyCode };

  const buildCurl = (method: string, path: string, body?: object): string => {
    const url = `${baseUrl}${path}`;
    const auth = apiVersion === 'v2'
      ? `  -H "X-API-Key: ${PK}" \\\n  -H "X-Timestamp: $(date +%s)" \\\n  -H "X-Signature: <hmac_sha256>"`
      : `  -H "X-API-Key: ${PK}" \\\n  -H "X-API-Secret: sk_your_api_secret"`;
    if (body) {
      return `curl -X ${method} "${url}" \\\n${auth} \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body, null, 2)}'`;
    }
    return `curl -X ${method} "${url}" \\\n${auth}`;
  };

  /* ── code snippets ── */
  const sigCode: Record<string,string> = {
    javascript: `const crypto = require('crypto');

const API_KEY    = '${PK}';
const SECRET_KEY = 'sk_your_api_secret';

function sign(method, path, body = {}) {
  const ts = Math.floor(Date.now() / 1000).toString();
  let payload = ts + method.toUpperCase() + '/api/v2' + path;
  if (Object.keys(body).length) payload += JSON.stringify(body);
  const sig = crypto.createHmac('sha256', SECRET_KEY).update(payload).digest('hex');
  return { 'X-Timestamp': ts, 'X-Signature': sig };
}

// Usage
const body    = { amount:500, bank_name:'SCB', bank_number:'1234567890', reference_id:'ORD-001' };
const headers = { 'X-API-Key': API_KEY, 'Content-Type': 'application/json', ...sign('POST', '/orders', body) };`,

    php: `<?php
function sign(string $method, string $path, array $body, string $secret): array {
    $ts      = time();
    $payload = $ts . strtoupper($method) . '/api/v2' . $path;
    if (!empty($body)) $payload .= json_encode($body);
    return ['X-Timestamp' => $ts, 'X-Signature' => hash_hmac('sha256', $payload, $secret)];
}

$body    = ['amount'=>500, 'bank_name'=>'SCB', 'bank_number'=>'1234567890', 'reference_id'=>'ORD-001'];
$sig     = sign('POST', '/orders', $body, 'sk_your_api_secret');
$headers = ["X-API-Key: ${PK}", "X-Timestamp: {$sig['X-Timestamp']}", "X-Signature: {$sig['X-Signature']}", "Content-Type: application/json"];
?>`,

    python: `import hmac, hashlib, json, time

API_KEY    = '${PK}'
SECRET_KEY = 'sk_your_api_secret'

def sign(method: str, path: str, body: dict = None) -> dict:
    ts      = str(int(time.time()))
    payload = ts + method.upper() + '/api/v2' + path
    if body:
        payload += json.dumps(body, separators=(',',':'), ensure_ascii=False)
    sig = hmac.new(SECRET_KEY.encode(), payload.encode(), hashlib.sha256).hexdigest()
    return {'X-Timestamp': ts, 'X-Signature': sig}

body    = {'amount':500, 'bank_name':'SCB', 'bank_number':'1234567890', 'reference_id':'ORD-001'}
headers = {'X-API-Key': API_KEY, 'Content-Type': 'application/json', **sign('POST', '/orders', body)}`,
  };

  const createOrderCode: Record<string,string> = {
    javascript: `const axios  = require('axios');
const crypto = require('crypto');

const API_KEY    = '${PK}';
const SECRET_KEY = 'sk_your_api_secret';
const BASE_URL   = '${baseUrlV2}';

function sign(method, path, body = {}) {
  const ts = Math.floor(Date.now() / 1000).toString();
  let p = ts + method.toUpperCase() + '/api/v2' + path;
  if (Object.keys(body).length) p += JSON.stringify(body);
  return { 'X-Timestamp': ts, 'X-Signature': crypto.createHmac('sha256', SECRET_KEY).update(p).digest('hex') };
}

async function createOrder() {
  const body = {
    amount:        500.00,
    reference_id:  'ORDER-' + Date.now(),
    bank_name:     'SCB',
    bank_number:   '1234567890',
    customer_name: 'สมชาย ใจดี',
  };
  const { data } = await axios.post(BASE_URL + '/orders', body, {
    headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json', ...sign('POST', '/orders', body) }
  });
  console.log('Order Number:', data.data.order_number);
  console.log('Payment URL: ', data.data.payment_url);
}
createOrder();`,

    php: `<?php
$apiKey    = '${PK}';
$apiSecret = 'sk_your_api_secret';
$baseUrl   = '${baseUrlV2}';

function sign($method, $path, $body, $secret) {
    $ts = time();
    $p  = $ts . strtoupper($method) . '/api/v2' . $path;
    if (!empty($body)) $p .= json_encode($body);
    return ['X-Timestamp' => $ts, 'X-Signature' => hash_hmac('sha256', $p, $secret)];
}

$body = ['amount'=>500.00, 'reference_id'=>'ORDER-'.time(), 'bank_name'=>'SCB', 'bank_number'=>'1234567890', 'customer_name'=>'สมชาย ใจดี'];
$sig  = sign('POST', '/orders', $body, $apiSecret);

$ch = curl_init("$baseUrl/orders");
curl_setopt_array($ch, [
    CURLOPT_POST           => true,
    CURLOPT_HTTPHEADER     => ["X-API-Key: $apiKey","X-Timestamp: {$sig['X-Timestamp']}","X-Signature: {$sig['X-Signature']}","Content-Type: application/json"],
    CURLOPT_POSTFIELDS     => json_encode($body),
    CURLOPT_RETURNTRANSFER => true,
]);
$r = json_decode(curl_exec($ch), true); curl_close($ch);
echo "Order:   " . $r['data']['order_number'] . "\\n";
echo "Pay URL: " . $r['data']['payment_url']  . "\\n";
?>`,

    python: `import hmac, hashlib, json, time, requests

API_KEY    = '${PK}'
API_SECRET = 'sk_your_api_secret'
BASE_URL   = '${baseUrlV2}'

def sign(method, path, body=None):
    ts = str(int(time.time()))
    p  = ts + method.upper() + '/api/v2' + path
    if body: p += json.dumps(body, separators=(',',':'), ensure_ascii=False)
    return {'X-Timestamp': ts, 'X-Signature': hmac.new(API_SECRET.encode(), p.encode(), hashlib.sha256).hexdigest()}

body    = {'amount':500.00, 'reference_id':f'ORDER-{int(time.time())}', 'bank_name':'SCB', 'bank_number':'1234567890', 'customer_name':'สมชาย ใจดี'}
headers = {'X-API-Key': API_KEY, 'Content-Type': 'application/json', **sign('POST', '/orders', body)}
resp    = requests.post(f'{BASE_URL}/orders', json=body, headers=headers).json()
print("Order:  ", resp['data']['order_number'])
print("Pay URL:", resp['data']['payment_url'])`,
  };

  const webhookCode: Record<string,string> = {
    javascript: `const express = require('express');
const crypto  = require('crypto');
const WEBHOOK_SECRET = 'your_webhook_secret';

app.post('/webhook/payment', express.raw({ type:'application/json' }), (req, res) => {
  const sig      = req.headers['x-webhook-signature'] || '';
  const expected = crypto.createHmac('sha256', WEBHOOK_SECRET).update(req.body).digest('hex');

  if (!crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(sig))) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const event = JSON.parse(req.body.toString());
  switch (event.event) {
    case 'order.paid':
      console.log(\`✅ Order \${event.data.order_number} paid ฿\${event.data.amount}\`);
      // fulfill order...
      break;
    case 'payout.completed':
      console.log(\`💸 Payout \${event.data.payout_number} completed\`);
      break;
  }
  res.json({ received: true });   // respond 200 within 10s
});`,

    php: `<?php
$webhookSecret = 'your_webhook_secret';
$payload       = file_get_contents('php://input');
$signature     = $_SERVER['HTTP_X_WEBHOOK_SIGNATURE'] ?? '';

if (!hash_equals(hash_hmac('sha256', $payload, $webhookSecret), $signature)) {
    http_response_code(401);
    exit(json_encode(['error' => 'Invalid signature']));
}

$event = json_decode($payload, true);
switch ($event['event']) {
    case 'order.paid':
        error_log("Order {$event['data']['order_number']} paid ฿{$event['data']['amount']}");
        break;
    case 'payout.completed':
        error_log("Payout {$event['data']['payout_number']} completed");
        break;
}
echo json_encode(['received' => true]);
?>`,

    python: `import hmac, hashlib, json
from flask import Flask, request, jsonify

app          = Flask(__name__)
WHOOK_SECRET = 'your_webhook_secret'

@app.route('/webhook/payment', methods=['POST'])
def handle_webhook():
    payload   = request.get_data()
    signature = request.headers.get('X-Webhook-Signature', '')
    expected  = hmac.new(WHOOK_SECRET.encode(), payload, hashlib.sha256).hexdigest()
    if not hmac.compare_digest(expected, signature):
        return jsonify(error='Invalid signature'), 401

    event = json.loads(payload)
    if event['event'] == 'order.paid':
        print(f"✅ Order {event['data']['order_number']} paid ฿{event['data']['amount']}")
    elif event['event'] == 'payout.completed':
        print(f"💸 Payout {event['data']['payout_number']} completed")

    return jsonify(received=True)   # respond 200 within 10s`,
  };

  const filteredTabs = NAV_TABS.filter(t =>
    !sidebarSearch || t.label.toLowerCase().includes(sidebarSearch.toLowerCase())
  );

  const LANG_ICONS = { javascript: faJsSquare, php: faPhp, python: faPython };

  const LangBar = () => (
    <div style={{ display:'flex', gap:'0.5rem', marginBottom:'0.875rem', flexWrap:'wrap' }}>
      {(['javascript','php','python'] as const).map(lang => (
        <button key={lang} className={`pdoc-lang-btn${selectedLang===lang?' pdoc-lang-active':''}`} onClick={() => setSelectedLang(lang)}>
          <FontAwesomeIcon icon={LANG_ICONS[lang]} style={{ fontSize:'0.9rem' }} />
          {lang.charAt(0).toUpperCase()+lang.slice(1)}
        </button>
      ))}
    </div>
  );

  /* ─────────── inline style helpers ─────────── */
  const card  = { background:'#ffffff', border:'1px solid #dfd5c5', borderRadius:'0.75rem', padding:'1.25rem', marginBottom:'1.25rem', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' } as React.CSSProperties;
  const muted = { fontSize:'0.825rem', color:'#6a5a48', lineHeight:1.65, marginBottom:'1rem' } as React.CSSProperties;
  const subHdr= { fontSize:'0.75rem', fontWeight:700, color:'#6a5a48', marginBottom:'0.625rem', display:'flex', alignItems:'center', gap:'0.375rem' } as React.CSSProperties;

  /* ─────────────────────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────────────────────── */
  return (
    <>
      <style>{STYLES}</style>
      <div className="pdoc-root" style={{ minHeight:'100vh' }}>

        {/* ══ HERO ══════════════════════════════════════════════ */}
        <div style={{ position:'relative', overflow:'hidden', background:'#f0e8d8', borderBottom:'1px solid #dfd5c5' }}>
          <div className="pdoc-hero-grid" style={{ position:'absolute', inset:0, pointerEvents:'none' }} />
          <div style={{ position:'absolute', top:-60, right:-60, width:320, height:320, borderRadius:'50%', background:'radial-gradient(circle, rgba(181,101,10,0.12) 0%, transparent 65%)', pointerEvents:'none' }} />

          <div style={{ position:'relative', zIndex:1, padding:'2rem' }}>
            {/* top bar */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.75rem', flexWrap:'wrap', gap:'0.75rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                <div style={{ width:36, height:36, borderRadius:'0.5rem', background:'linear-gradient(135deg,#9a4800,#c87012)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 10px rgba(181,101,10,0.30)' }}>
                  <FontAwesomeIcon icon={faRocket} style={{ color:'#fff8f0', fontSize:'0.875rem' }} />
                </div>
                <div>
                  <div style={{ fontSize:'0.875rem', fontWeight:800, color:'#1c1510', fontFamily:'IBM Plex Mono,monospace', letterSpacing:'0.03em' }}>go2pay</div>
                  <div style={{ fontSize:'0.6rem', color:'#9a8870', fontFamily:'IBM Plex Mono,monospace', letterSpacing:'0.12em', textTransform:'uppercase' }}>Developer API</div>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                <span className="pdoc-status-dot" style={{ width:7, height:7, borderRadius:'50%', background:'#15803d', display:'block' }} />
                <span style={{ fontSize:'0.65rem', color:'#15803d', fontFamily:'IBM Plex Mono,monospace', letterSpacing:'0.08em', fontWeight:600 }}>ALL SYSTEMS OPERATIONAL</span>
              </div>
            </div>

            {/* headline + stats */}
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'2rem' }}>
              <div style={{ maxWidth:560 }}>
                <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1rem', flexWrap:'wrap' }}>
                  {[
                    ['REST API v2.0','rgba(181,101,10,0.12)','rgba(181,101,10,0.3)','#9a5000'],
                    ['STABLE','rgba(21,128,61,0.10)','rgba(21,128,61,0.3)','#15803d'],
                  ].map(([label,bg,border,color]) => (
                    <span key={String(label)} style={{ padding:'0.2rem 0.75rem', background:bg as string, border:`1px solid ${border}`, borderRadius:999, fontSize:'0.6rem', fontFamily:'IBM Plex Mono,monospace', color:color as string, letterSpacing:'0.1em', fontWeight:700 }}>{String(label)}</span>
                  ))}
                </div>
                <h1 style={{ fontSize:'clamp(1.8rem,4vw,2.75rem)', fontWeight:800, color:'#1c1510', lineHeight:1.12, letterSpacing:'-0.025em', margin:'0 0 0.875rem', fontFamily:'IBM Plex Sans,sans-serif' }}>
                  Payment Gateway<br />
                  <span style={{ color:'#b5650a' }}>API Reference</span>
                </h1>
                <p style={{ fontSize:'0.9rem', color:'#6a5a48', lineHeight:1.7, margin:0 }}>
                  Thai payment infrastructure for developers. Accept PromptPay QR&nbsp;payments,
                  manage payouts, and receive real-time webhooks — all through a clean REST API.
                </p>
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem', minWidth:200 }}>
                {[
                  { icon:faBolt,         val:'~50ms',       sub:'Avg Response' },
                  { icon:faShieldHalved, val:'HMAC-SHA256', sub:'v2 Signature' },
                  { icon:faGlobe,        val:'99.9%',       sub:'SLA Uptime'   },
                ].map((s,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.6rem 1rem', background:'rgba(255,255,255,0.75)', border:'1px solid #dfd5c5', borderRadius:'0.5rem' }}>
                    <FontAwesomeIcon icon={s.icon} style={{ color:'#b5650a', fontSize:'0.85rem', width:16 }} />
                    <span style={{ fontSize:'0.85rem', fontWeight:700, color:'#1c1510', fontFamily:'IBM Plex Mono,monospace' }}>{s.val}</span>
                    <span style={{ fontSize:'0.68rem', color:'#9a8870', marginLeft:'auto' }}>{s.sub}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* base URL bar */}
            <div style={{ display:'flex', alignItems:'center', gap:'1rem', padding:'0.55rem 1rem', background:'rgba(255,255,255,0.85)', border:'1px solid #dfd5c5', borderRadius:'0.5rem', marginTop:'1.5rem', flexWrap:'wrap' }}>
              <span style={{ fontSize:'0.6rem', fontFamily:'IBM Plex Mono,monospace', color:'#9a8870', letterSpacing:'0.1em', textTransform:'uppercase', flexShrink:0 }}>Base URL</span>
              <code style={{ fontSize:'0.78rem', fontFamily:'IBM Plex Mono,monospace', color:'#3d2e1e', fontWeight:500, flex:1 }}>{baseUrl}</code>
              <div style={{ display:'flex', gap:'0.375rem', marginLeft:'auto' }}>
                {(['v2','v1'] as const).map(v => (
                  <button key={v} onClick={() => setApiVersion(v)} className="pdoc-ver-btn"
                    style={{ background: apiVersion===v ? (v==='v2'?'rgba(21,128,61,0.15)':'rgba(100,90,80,0.12)') : 'transparent', color: apiVersion===v ? (v==='v2'?'#15803d':'#6a5a48') : '#9a8870', width:36 }}
                  >{v.toUpperCase()}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══ LAYOUT ══════════════════════════════════════════════ */}
        <div style={{ display:'flex', position:'relative' }}>

          {/* SIDEBAR */}
          <aside className={`pdoc-sidebar${mobileNavOpen?' pdoc-open':''}`}
            style={{ width:248, flexShrink:0, background:'#ede7d8', borderRight:'1px solid #dfd5c5', height:'100vh', position:'sticky', top:0, overflowY:'auto', padding:'1.25rem 0.625rem' }}>

            {/* search */}
            <div style={{ position:'relative', marginBottom:'1.25rem' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position:'absolute', left:'0.625rem', top:'50%', transform:'translateY(-50%)', color:'#a89880', fontSize:'0.65rem', pointerEvents:'none' }} />
              <input type="text" placeholder="Search docs…" value={sidebarSearch}
                onChange={e => setSidebarSearch(e.target.value)}
                className="pdoc-search-input"
                style={{ width:'100%', paddingLeft:'2rem', paddingRight:'0.75rem', paddingTop:'0.45rem', paddingBottom:'0.45rem', background:'#f7f2ea', border:'1px solid #dfd5c5', borderRadius:'0.375rem', color:'#3a3028', fontSize:'0.75rem', fontFamily:'IBM Plex Sans,sans-serif', outline:'none', boxSizing:'border-box' }}
              />
            </div>

            {/* nav */}
            <nav>
              {NAV_CATS.map(cat => {
                const tabs = filteredTabs.filter(t => t.category === cat);
                if (!tabs.length) return null;
                return (
                  <div key={cat} style={{ marginBottom:'1.25rem' }}>
                    <div style={{ fontSize:'0.58rem', fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', color:'#b0a090', padding:'0 0.75rem', marginBottom:'0.2rem', fontFamily:'IBM Plex Mono,monospace' }}>{cat}</div>
                    {tabs.map(tab => (
                      <button key={tab.id} className={`pdoc-sidebar-link${activeTab===tab.id?' pdoc-active':''}`}
                        onClick={() => { setActiveTab(tab.id); setMobileNavOpen(false); }}>
                        <FontAwesomeIcon icon={tab.icon} style={{ fontSize:'0.68rem', width:14, flexShrink:0, opacity: activeTab===tab.id ? 1 : 0.5 }} />
                        <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis' }}>{tab.label}</span>
                        {activeTab===tab.id && <FontAwesomeIcon icon={faChevronRight} style={{ fontSize:'0.55rem', opacity:0.4, flexShrink:0 }} />}
                      </button>
                    ))}
                  </div>
                );
              })}
            </nav>

            {/* version toggle */}
            <div style={{ margin:'1rem 0.375rem 0', padding:'0.75rem', background:'#f7f2ea', border:'1px solid #dfd5c5', borderRadius:'0.5rem' }}>
              <div style={{ fontSize:'0.58rem', fontFamily:'IBM Plex Mono,monospace', letterSpacing:'0.12em', color:'#b0a090', textTransform:'uppercase', marginBottom:'0.5rem' }}>API Version</div>
              <div style={{ display:'flex', gap:'0.375rem' }}>
                {(['v2','v1'] as const).map(v => (
                  <button key={v} onClick={() => setApiVersion(v)} className="pdoc-ver-btn"
                    style={{ background: apiVersion===v ? (v==='v2'?'rgba(21,128,61,0.15)':'rgba(100,90,80,0.15)') : 'transparent', color: apiVersion===v ? (v==='v2'?'#15803d':'#6a5a48') : '#b0a090' }}
                  >{v.toUpperCase()}</button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div style={{ margin:'1rem 0.375rem 0', padding:'0.875rem', background:'rgba(181,101,10,0.07)', border:'1px solid rgba(181,101,10,0.22)', borderRadius:'0.5rem' }}>
              <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#9a5000', marginBottom:'0.3rem' }}>Ready to integrate?</div>
              <div style={{ fontSize:'0.7rem', color:'#7a6a58', lineHeight:1.5, marginBottom:'0.625rem' }}>Sign up and get your API keys in minutes.</div>
              <a href="/login" style={{ display:'block', textAlign:'center', padding:'0.45rem', background:'rgba(181,101,10,0.12)', border:'1px solid rgba(181,101,10,0.3)', borderRadius:'0.375rem', color:'#9a5000', fontSize:'0.72rem', fontWeight:700, textDecoration:'none', fontFamily:'IBM Plex Mono,monospace', letterSpacing:'0.04em' }}>
                GET API KEYS →
              </a>
            </div>
          </aside>

          {/* mobile backdrop */}
          {mobileNavOpen && <div onClick={() => setMobileNavOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:39 }} />}

          {/* CONTENT */}
          <main className="pdoc-main" ref={contentRef}
            style={{ flex:1, minWidth:0, padding:'2.25rem 2.5rem', maxWidth:840 }}>

            {/* mobile FAB */}
            <button className="pdoc-fab" onClick={() => setMobileNavOpen(true)}
              style={{ display:'none', position:'fixed', bottom:'1.25rem', right:'1.25rem', zIndex:35, width:48, height:48, borderRadius:'50%', background:'#b5650a', border:'none', color:'#fff', fontSize:'1rem', cursor:'pointer', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 16px rgba(181,101,10,0.35)' }}>
              <FontAwesomeIcon icon={faBars} />
            </button>

            <div className="pdoc-content-in" key={activeTab}>

              {/* ════ OVERVIEW ════════════════════════════════════ */}
              {activeTab === 'overview' && (
                <div>
                  <SectionTitle icon={faStar}>Welcome to go2pay API</SectionTitle>
                  <p style={{ color:'#5a4e42', fontSize:'0.875rem', lineHeight:1.75, marginBottom:'2rem' }}>
                    A REST API for Thai payment processing. Generate PromptPay QR codes, match bank transfers automatically, and receive instant webhooks — built for developers who need reliable, low-latency payment infrastructure.
                  </p>

                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:'1rem', marginBottom:'2rem' }}>
                    {[
                      { icon:faBolt,      title:'Fast Matching',   desc:'Auto-match bank transfers in seconds',  grad:'#b5650a,#d4870a' },
                      { icon:faLock,      title:'HMAC Signatures', desc:'v2 API with SHA-256 request signing',   grad:'#15803d,#16a34a' },
                      { icon:faMobileAlt, title:'PromptPay QR',    desc:'Dynamic QR with unique cent modifier',  grad:'#0284c7,#0369a1' },
                      { icon:faBell,      title:'Webhooks',        desc:'Real-time notifications on every event',grad:'#7c3aed,#6d28d9' },
                    ].map((f,i) => (
                      <div key={i} style={{ background:'#ffffff', border:'1px solid #dfd5c5', borderRadius:'0.75rem', padding:'1.125rem', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', cursor:'default', transition:'box-shadow 0.2s' }}>
                        <div style={{ width:40, height:40, borderRadius:'0.5rem', background:`linear-gradient(135deg,${f.grad})`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'0.75rem', boxShadow:'0 3px 10px rgba(0,0,0,0.12)' }}>
                          <FontAwesomeIcon icon={f.icon} style={{ color:'#fff', fontSize:'1rem' }} />
                        </div>
                        <div style={{ fontSize:'0.8rem', fontWeight:700, color:'#1c1510', marginBottom:'0.3rem' }}>{f.title}</div>
                        <div style={{ fontSize:'0.73rem', color:'#8a7a68', lineHeight:1.5 }}>{f.desc}</div>
                      </div>
                    ))}
                  </div>

                  <div style={card}>
                    <h3 style={{ fontSize:'0.875rem', fontWeight:700, color:'#b5650a', display:'flex', alignItems:'center', gap:'0.5rem', margin:'0 0 1rem' }}>
                      <FontAwesomeIcon icon={faArrowRight} style={{ fontSize:'0.75rem' }} /> Quick Start
                    </h3>
                    <ol style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                      {[
                        'Create a merchant account and obtain your API Key & Secret',
                        'Choose API version — V2 (HMAC signature) is recommended',
                        'Generate a HMAC-SHA256 signature for each request',
                        'Call POST /orders to create a PromptPay QR payment',
                        'Listen for order.paid webhook to fulfill the order',
                      ].map((step,i) => (
                        <li key={i} style={{ display:'flex', alignItems:'flex-start', gap:'0.875rem' }}>
                          <span style={{ width:26, height:26, borderRadius:'0.375rem', background:'rgba(181,101,10,0.10)', border:'1px solid rgba(181,101,10,0.22)', color:'#9a5000', fontSize:'0.7rem', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontFamily:'IBM Plex Mono,monospace' }}>{i+1}</span>
                          <span style={{ fontSize:'0.825rem', color:'#5a4e42', lineHeight:1.55, paddingTop:'0.2rem' }}>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <InfoCard type="success" title="Multi-tenant">
                    The API base URL is derived from your merchant portal domain. If your portal is at{' '}
                    <code style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:'0.78rem' }}>merchant.go2pay.tech</code>, your API base is{' '}
                    <code style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:'0.78rem' }}>api.go2pay.tech/api/v2</code>.
                  </InfoCard>
                </div>
              )}

              {/* ════ AUTHENTICATION ══════════════════════════════ */}
              {activeTab === 'authentication' && (
                <div>
                  <SectionTitle icon={faKey}>Authentication</SectionTitle>
                  <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1.5rem', flexWrap:'wrap' }}>
                    {([['v2','V2 — HMAC Signature (Recommended)','rgba(21,128,61,0.10)','rgba(21,128,61,0.3)','#15803d'],['v1','V1 — API Secret (Legacy)','rgba(100,90,80,0.10)','rgba(100,90,80,0.3)','#6a5a48']] as const).map(([v,label,bg,border,color]) => (
                      <button key={v} onClick={() => setApiVersion(v as 'v1'|'v2')}
                        style={{ padding:'0.5rem 1rem', borderRadius:'0.5rem', border:`1px solid ${apiVersion===v?border:'#dfd5c5'}`, background:apiVersion===v?bg:'#ffffff', color:apiVersion===v?color:'#8a7a68', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif', transition:'all 0.15s' }}>
                        {label}
                      </button>
                    ))}
                  </div>

                  {apiVersion === 'v2' ? (
                    <>
                      <InfoCard type="success" title="V2 Recommended">
                        Your Secret Key never travels over the network. Each request is signed with HMAC-SHA256 — even if traffic is intercepted, the signature cannot be replayed.
                      </InfoCard>
                      <div style={card}>
                        <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#1c1510', marginBottom:'0.75rem' }}>Required Headers</div>
                        <CodeBlock {...CB} id="v2-headers" label="request headers"
                          code={`X-API-Key:    ${PK}\nX-Timestamp:  <unix_seconds>          // current Unix timestamp\nX-Signature:  <hmac_sha256_hex>       // see algorithm below\nContent-Type: application/json`} />
                      </div>
                      <div style={card}>
                        <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#1c1510', marginBottom:'0.625rem' }}>Signature Algorithm</div>
                        <ol style={{ margin:'0 0 1rem', padding:'0 0 0 1.25rem', fontSize:'0.8rem', color:'#5a4e42', lineHeight:1.7 }}>
                          <li>Concatenate: <FC>timestamp + METHOD + /api/v2 + path</FC></li>
                          <li>If body exists, append <FC>JSON.stringify(body)</FC> (no extra whitespace)</li>
                          <li>Sign with <FC>HMAC-SHA256(secret_key, payload)</FC></li>
                          <li>Hex-encode the digest → set as <FC>X-Signature</FC></li>
                        </ol>
                        <LangBar />
                        <CodeBlock {...CB} id={`sig-${selectedLang}`} label={selectedLang} code={sigCode[selectedLang]} />
                      </div>
                      <InfoCard type="warning" title="Replay Protection">
                        The server rejects requests where the timestamp differs from server time by more than <strong>5 minutes</strong>. Keep clocks synced (NTP).
                      </InfoCard>
                      <InfoCard type="tip" title="Secret Key Safety">
                        Never expose <FC>SECRET_KEY</FC> in frontend code or version control. Use environment variables on your server.
                      </InfoCard>
                    </>
                  ) : (
                    <>
                      <InfoCard type="warning" title="V1 — Legacy">
                        V1 sends the API Secret in every request header. Migrate to V2 for better security. Both versions remain supported.
                      </InfoCard>
                      <div style={card}>
                        <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#1c1510', marginBottom:'0.75rem' }}>Required Headers</div>
                        <CodeBlock {...CB} id="v1-headers" label="request headers"
                          code={`X-API-Key:    ${PK}\nX-API-Secret: sk_your_api_secret\nContent-Type: application/json`} />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ════ ORDERS API ══════════════════════════════════ */}
              {activeTab === 'orders' && (
                <div>
                  <SectionTitle icon={faRocket}>Orders API (Payin)</SectionTitle>
                  <p style={{ ...muted, marginBottom:'1.5rem' }}>
                    Create PromptPay QR payments and check their status. Each order gets a unique amount modifier (e.g. ฿100.00 → ฿100.23) for automatic bank-transfer matching.
                  </p>

                  <EndpointCard method="POST" path="/orders" cb={CB} curlId="curl-post-orders"
                    curl={buildCurl('POST','/orders',{ amount:500, reference_id:'ORDER-001', bank_name:'SCB', bank_number:'1234567890', customer_name:'สมชาย ใจดี' })}>
                    <p style={muted}>Creates a new payment order and returns a PromptPay QR code.</p>
                    <div style={subHdr}><FontAwesomeIcon icon={faPencilAlt} style={{ color:'#9a8870' }} /> Request Body</div>
                    <ApiTable headers={['Field','Type','Required','Description']} rows={[
                      [<FC>amount</FC>,        'number',<Req/>,'Payment amount in Thai Baht'],
                      [<FC>reference_id</FC>,  'string',<Req/>,'Your unique order identifier'],
                      [<FC>bank_name</FC>,      'string',<Req/>,'Receiving bank short code (SCB, KBANK…)'],
                      [<FC>bank_number</FC>,    'string',<Req/>,'Receiving bank account number'],
                      [<FC>customer_name</FC>,  'string',<Opt/>,"Customer's name"],
                      [<FC>customer_email</FC>, 'string',<Opt/>,"Customer's email"],
                      [<FC>customer_phone</FC>, 'string',<Opt/>,"Customer's phone"],
                      [<FC>callback_url</FC>,   'string',<Opt/>,'Override webhook URL for this order'],
                      [<FC>redirect_url</FC>,   'string',<Opt/>,'Redirect URL after payment'],
                    ]} />
                    <div style={{ ...subHdr, color:'#15803d' }}><FontAwesomeIcon icon={faCheckCircle} /> Response (201)</div>
                    <CodeBlock {...CB} id="orders-create-res" label="response.json" code={`{
  "success": true,
  "data": {
    "order_number":       "ORD_abc123xyz",
    "amount":             100.00,
    "total_amount":       100.45,
    "qr_code":            "data:image/png;base64,...",
    "status":             "pending",
    "expires_at":         "2025-10-09T12:35:29.000Z",
    "expires_in_seconds": 300,
    "payment_url":        "https://pay.go2pay.tech/pay/ORD_abc123xyz",
    "redirect_url":       "https://your-site.com/thank-you"
  }
}`} />
                  </EndpointCard>

                  <EndpointCard method="GET" path="/orders/:order_number" cb={CB} curlId="curl-get-order"
                    curl={buildCurl('GET','/orders/ORD_abc123xyz')}>
                    <p style={muted}>Retrieve the current status of a payment order.</p>
                    <CodeBlock {...CB} id="orders-get-res" label="response.json" code={`{
  "success": true,
  "data": {
    "order_number": "ORD_abc123xyz",
    "amount":       100.00,
    "total_amount": 100.45,
    "status":       "paid",
    "created_at":   "2025-10-09T12:30:29.000Z",
    "expires_at":   "2025-10-09T12:35:29.000Z",
    "paid_at":      "2025-10-09T12:32:15.000Z",
    "seconds_left": 0,
    "is_expired":   false
  }
}`} />
                    <div style={subHdr}><FontAwesomeIcon icon={faChartBar} style={{ color:'#9a8870' }} /> Status Values</div>
                    <div>
                      <SBadge bg="rgba(181,101,10,0.10)" color="#9a5000" icon={faClock}>pending</SBadge>
                      <SBadge bg="rgba(21,128,61,0.10)"  color="#15803d" icon={faCheckCircle}>paid</SBadge>
                      <SBadge bg="rgba(100,90,80,0.10)"  color="#6a5a48" icon={faTimesCircle}>expired</SBadge>
                      <SBadge bg="rgba(220,38,38,0.10)"  color="#b91c1c" icon={faBan}>cancelled</SBadge>
                    </div>
                  </EndpointCard>

                  <EndpointCard method="GET" path="/orders" cb={CB} curlId="curl-list-orders"
                    curl={buildCurl('GET','/orders?limit=20&status=paid')}>
                    <p style={muted}>List orders with optional filtering.</p>
                    <ApiTable headers={['Field','Type','Default','Description']} rows={[
                      [<FC>status</FC>, 'string','—',  'Filter: pending | paid | expired | cancelled'],
                      [<FC>limit</FC>,  'number','50', 'Max results to return'],
                      [<FC>offset</FC>, 'number','0',  'Pagination offset'],
                    ]} />
                  </EndpointCard>
                </div>
              )}

              {/* ════ SLIP ORDER ══════════════════════════════════ */}
              {activeTab === 'slip-order' && (
                <div>
                  <SectionTitle icon={faClipboardList}>Slip Order API</SectionTitle>
                  <p style={{ ...muted, marginBottom:'1.25rem' }}>For merchants who require manual slip verification. The customer transfers money and uploads a bank transfer slip for server-side validation.</p>
                  <InfoCard type="info" title="Flow">
                    <ol style={{ margin:0, paddingLeft:'1.25rem', lineHeight:1.8 }}>
                      <li>Call <FC>POST /slip-orders</FC> — receive an order with assigned bank account info</li>
                      <li>Customer transfers the exact amount and provides the slip</li>
                      <li>Call <FC>POST /orders/:order_number/verify-slip</FC> — API validates the slip</li>
                    </ol>
                  </InfoCard>
                  <InfoCard type="warning" title="Important">
                    Do <strong>not</strong> ask the customer to choose a bank before creating the order. Bank assignment happens server-side.
                  </InfoCard>

                  <EndpointCard method="POST" path="/slip-orders" cb={CB} curlId="curl-post-slip-orders"
                    curl={buildCurl('POST','/slip-orders',{ amount:500, reference_id:'SLIP-001', bank_name:'SCB', bank_number:'1234567890', customer_name:'สมชาย ใจดี' })}>
                    <p style={muted}>Creates a slip-verification order. Response includes the bank account the customer should transfer to.</p>
                    <ApiTable headers={['Field','Type','Required','Description']} rows={[
                      [<FC>amount</FC>,        'number',<Req/>,'Exact transfer amount in THB'],
                      [<FC>bank_name</FC>,      'string',<Req/>,'Your receiving bank (short code)'],
                      [<FC>bank_number</FC>,    'string',<Req/>,'Your receiving account number'],
                      [<FC>reference_id</FC>,   'string',<Req/>,'Your unique order reference'],
                      [<FC>customer_name</FC>,  'string',<Opt/>,'Customer name'],
                      [<FC>customer_email</FC>, 'string',<Opt/>,'Customer email'],
                      [<FC>redirect_url</FC>,   'string',<Opt/>,'Post-payment redirect URL'],
                    ]} />
                    <CodeBlock {...CB} id="slip-res" label="response.json" code={`{
  "success": true,
  "data": {
    "order_number": "ORD_abc123xyz",
    "amount":       500.00,
    "slip_only":    true,
    "bank_info": {
      "bank_name":      "ไทยพาณิชย์",
      "bank_code":      "014",
      "account_number": "123-456789-0"
    },
    "status":             "pending",
    "expires_in_seconds": 600,
    "payment_url":        "https://pay.go2pay.tech/pay/ORD_abc123xyz"
  }
}`} />
                  </EndpointCard>

                  <EndpointCard method="POST" path="/orders/:order_number/verify-slip" cb={CB} curlId="curl-verify-slip"
                    curl={buildCurl('POST','/orders/ORD_abc123xyz/verify-slip',{ qr_string:'<qr_data_from_slip>' })}>
                    <p style={muted}>Verify a transfer slip. Send one of: QR string, Base64 image, or multipart file upload.</p>
                    <ApiTable headers={['Field','Type','Description']} rows={[
                      [<FC>qr_string</FC>,   'string','Raw QR data scanned from the slip (highest priority)'],
                      [<FC>imageBase64</FC>, 'string','Base64-encoded slip image'],
                      [<FC>file</FC>,        'file',  'Multipart slip image (JPG/PNG) — Content-Type: multipart/form-data'],
                    ]} />
                    <InfoCard type="tip" title="Priority">Send only one field. Server tries: qr_string → imageBase64 → file.</InfoCard>
                  </EndpointCard>
                </div>
              )}

              {/* ════ PAYOUT API ══════════════════════════════════ */}
              {activeTab === 'payout' && (
                <div>
                  <SectionTitle icon={faMoneyBillTransfer}>Payout API</SectionTitle>
                  <p style={{ ...muted, marginBottom:'1.25rem' }}>Programmatically request bank transfers to any Thai bank account. Payouts are funded from your payout wallet balance.</p>
                  <InfoCard type="info" title="Payout Wallet">
                    Payouts debit your <strong>payout wallet</strong> (separate from the collection wallet). Top it up via the merchant portal before creating payouts.
                  </InfoCard>

                  <EndpointCard method="POST" path="/payout" cb={CB} curlId="curl-post-payout"
                    curl={buildCurl('POST','/payout',{ amount:1000, bank_account_name:'นาย A', bank_account_number:'1234567890', bank_name:'SCB', reference_id:'PAY-001' })}>
                    <ApiTable headers={['Field','Type','Required','Description']} rows={[
                      [<FC>amount</FC>,              'number',<Req/>,'Transfer amount in THB'],
                      [<FC>bank_account_name</FC>,   'string',<Req/>,"Recipient's full name"],
                      [<FC>bank_account_number</FC>, 'string',<Req/>,"Recipient's account number"],
                      [<FC>bank_name</FC>,            'string',<Req/>,"Recipient's bank (short code)"],
                      [<FC>reference_id</FC>,         'string',<Opt/>,'Your unique reference'],
                      [<FC>note</FC>,                 'string',<Opt/>,'Internal note (not sent to bank)'],
                      [<FC>callback_url</FC>,         'string',<Opt/>,'Override webhook URL for this payout'],
                    ]} />
                    <CodeBlock {...CB} id="payout-res" label="response.json" code={`{
  "success": true,
  "data": {
    "payout_number":       "PAYOUT20251009001",
    "amount":              1000.00,
    "fee_amount":          0,
    "net_amount":          1000.00,
    "status":              "pending",
    "bank_account_name":   "นาย A",
    "bank_account_number": "1234567890",
    "bank_name":           "SCB",
    "created_at":          "2025-10-09T12:30:29.000Z"
  }
}`} />
                  </EndpointCard>

                  <EndpointCard method="GET" path="/payout/:payout_number" cb={CB} curlId="curl-get-payout"
                    curl={buildCurl('GET','/payout/PAYOUT20251009001')}>
                    <p style={muted}>Check status of a specific payout.</p>
                    <div>
                      <SBadge bg="rgba(181,101,10,0.10)" color="#9a5000" icon={faClock}>pending</SBadge>
                      <SBadge bg="rgba(2,132,199,0.10)"  color="#0369a1" icon={faCheckCircle}>approved</SBadge>
                      <SBadge bg="rgba(21,128,61,0.10)"  color="#15803d" icon={faCheckCircle}>completed</SBadge>
                      <SBadge bg="rgba(220,38,38,0.10)"  color="#b91c1c" icon={faBan}>rejected</SBadge>
                    </div>
                  </EndpointCard>

                  <EndpointCard method="GET" path="/payouts" cb={CB} curlId="curl-list-payouts"
                    curl={buildCurl('GET','/payouts?limit=20')}>
                    <p style={muted}>List payouts. Supports <FC>status</FC>, <FC>limit</FC>, <FC>offset</FC> query params.</p>
                  </EndpointCard>
                </div>
              )}

              {/* ════ BALANCE API ═════════════════════════════════ */}
              {activeTab === 'balance' && (
                <div>
                  <SectionTitle icon={faWallet}>Balance API</SectionTitle>
                  <EndpointCard method="GET" path="/wallet" cb={CB} curlId="curl-get-wallet"
                    curl={buildCurl('GET','/wallet')}>
                    <p style={muted}>Returns your current wallet balances, transaction totals, and freeze records.</p>
                    <CodeBlock {...CB} id="balance-res" label="response.json" code={`{
  "success": true,
  "data": {
    "balance":             1234.56,
    "payout_balance":       500.00,
    "total_credit":        5000.00,
    "total_debit":         3765.44,
    "total_payout_credit": 1000.00,
    "total_payout_debit":   500.00,
    "updated_at":          "2025-10-09T12:45:00.000Z",
    "freeze_summary": {
      "count": 2, "total_frozen": 800.00,
      "total_returned": 600.00, "total_deducted": 200.00,
      "items": [
        { "freeze_date":"2025-10-01","status":"frozen",  "total_frozen":300.00,"returned_amount":null,  "deducted_amount":null   },
        { "freeze_date":"2025-09-01","status":"unfrozen","total_frozen":500.00,"returned_amount":400.00,"deducted_amount":100.00 }
      ]
    }
  }
}`} />
                  </EndpointCard>
                </div>
              )}

              {/* ════ BANKS API ═══════════════════════════════════ */}
              {activeTab === 'banks' && (
                <div>
                  <SectionTitle icon={faBuildingColumns}>Banks API</SectionTitle>
                  <EndpointCard method="GET" path="/banks" cb={CB} curlId="curl-get-banks"
                    curl={buildCurl('GET','/banks')}>
                    <p style={muted}>Returns the list of supported Thai banks for payin orders.</p>
                    <CodeBlock {...CB} id="banks-res" label="response.json" code={`{
  "success": true,
  "data": [
    { "bank_name":"กรุงเทพ",       "short_name":"BBL",   "bank_code":"002" },
    { "bank_name":"กสิกรไทย",     "short_name":"KBANK", "bank_code":"004" },
    { "bank_name":"กรุงไทย",      "short_name":"KTB",   "bank_code":"006" },
    { "bank_name":"ทีเอ็มบีธนชาต","short_name":"TTB",   "bank_code":"011" },
    { "bank_name":"ไทยพาณิชย์",   "short_name":"SCB",   "bank_code":"014" },
    { "bank_name":"กรุงศรี",      "short_name":"BAY",   "bank_code":"025" },
    { "bank_name":"ออมสิน",       "short_name":"GSB",   "bank_code":"030" }
  ],
  "total": 35
}`} />
                    <InfoCard type="tip" title="Usage">
                      Use the <code style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:'0.75rem' }}>short_name</code> value (e.g. <FC>SCB</FC>) in the <FC>bank_name</FC> field when creating orders or payouts.
                    </InfoCard>
                  </EndpointCard>
                </div>
              )}

              {/* ════ SYSTEM STATUS ═══════════════════════════════ */}
              {activeTab === 'system-status' && (
                <div>
                  <SectionTitle icon={faCogs}>System Status API</SectionTitle>
                  <p style={{ ...muted, marginBottom:'1.25rem' }}>Check whether deposit, payout, and settlement systems are open, and retrieve minimum transaction amounts.</p>
                  <InfoCard type="tip" title="Best Practice">
                    Poll this endpoint before creating orders or payouts to check system availability and minimum amounts.
                  </InfoCard>
                  <EndpointCard method="GET" path="/system-status" cb={CB} curlId="curl-system-status"
                    curl={buildCurl('GET','/system-status')}>
                    <CodeBlock {...CB} id="status-res" label="response.json" code={`{
  "success": true,
  "data": {
    "deposit_enabled":       true,
    "payout_enabled":        true,
    "settlement_enabled":    true,
    "min_deposit_amount":    300,
    "min_payout_amount":     300,
    "min_settlement_amount": 300
  }
}`} />
                    <ApiTable headers={['Field','Type','Description']} rows={[
                      [<FC>deposit_enabled</FC>,       'boolean','Whether the deposit system accepts new orders'],
                      [<FC>payout_enabled</FC>,        'boolean','Whether payout requests are being processed'],
                      [<FC>settlement_enabled</FC>,    'boolean','Whether merchant settlement is available'],
                      [<FC>min_deposit_amount</FC>,    'number', 'Minimum order amount in THB (0 = no limit)'],
                      [<FC>min_payout_amount</FC>,     'number', 'Minimum payout amount in THB'],
                      [<FC>min_settlement_amount</FC>, 'number', 'Minimum settlement withdrawal in THB'],
                    ]} />
                  </EndpointCard>
                </div>
              )}

              {/* ════ WEBHOOKS ════════════════════════════════════ */}
              {activeTab === 'webhook' && (
                <div>
                  <SectionTitle icon={faBell}>Webhooks</SectionTitle>
                  <p style={{ ...muted, marginBottom:'1.25rem' }}>
                    go2pay POSTs signed JSON to your callback URL when order or payout status changes. Your endpoint must respond HTTP 200 within <strong style={{ color:'#1c1510' }}>10 seconds</strong>.
                  </p>
                  <InfoCard type="warning" title="Retries">
                    Failed deliveries are retried up to 5 times with exponential backoff. Implement idempotency using <FC>order_number</FC> or <FC>payout_number</FC>.
                  </InfoCard>

                  <div style={card}>
                    <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#1c1510', marginBottom:'0.75rem' }}>Event Types</div>
                    <ApiTable headers={['Event','When']} rows={[
                      [<FC>order.paid</FC>,      'A payin order is matched and confirmed'],
                      [<FC>order.expired</FC>,   'A payin order expired without payment'],
                      [<FC>payout.approved</FC>, 'An admin approved the payout request'],
                      [<FC>payout.completed</FC>,'The bank transfer completed successfully'],
                      [<FC>payout.rejected</FC>, 'Admin rejected the payout request'],
                    ]} />
                  </div>

                  <div style={card}>
                    <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#1c1510', marginBottom:'0.75rem' }}>Webhook Payload — order.paid</div>
                    <CodeBlock {...CB} id="webhook-payload" label="POST to your callback_url" code={`{
  "event":     "order.paid",
  "timestamp": "2025-10-09T12:32:15.000Z",
  "data": {
    "order_number":  "ORD_abc123xyz",
    "reference_id":  "YOUR-ORDER-123",
    "amount":        100.00,
    "total_amount":  100.45,
    "status":        "paid",
    "paid_at":       "2025-10-09T12:32:15.000Z",
    "customer_name": "สมชาย ใจดี",
    "bank_name":     "SCB"
  }
}`} />
                  </div>

                  <div style={card}>
                    <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#1c1510', marginBottom:'0.5rem' }}>Signature Verification</div>
                    <p style={{ ...muted, marginBottom:'0.875rem' }}>
                      Each webhook includes an{' '}
                      <code style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:'0.75rem', background:'#f7f2ea', padding:'0 0.3rem', borderRadius:3 }}>X-Webhook-Signature</code>{' '}
                      header. Verify it to prevent forgery.
                    </p>
                    <LangBar />
                    <CodeBlock {...CB} id={`wh-${selectedLang}`} label={selectedLang} code={webhookCode[selectedLang]} />
                  </div>
                </div>
              )}

              {/* ════ ERROR CODES ═════════════════════════════════ */}
              {activeTab === 'errors' && (
                <div>
                  <SectionTitle icon={faExclamationTriangle}>Error Codes</SectionTitle>
                  <p style={{ ...muted, marginBottom:'1.5rem' }}>
                    All error responses shape:{' '}
                    <code style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:'0.78rem', background:'#f7f2ea', padding:'0.1rem 0.4rem', borderRadius:3, color:'#3d2e1e' }}>{'{ "success": false, "message": "..." }'}</code>
                  </p>

                  <div style={card}>
                    <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#1c1510', marginBottom:'0.75rem' }}>HTTP Status Codes</div>
                    <ApiTable headers={['Status','Meaning','Common Causes']} rows={[
                      [<span style={{ fontFamily:'IBM Plex Mono,monospace', color:'#9a5000', fontWeight:700 }}>400</span>,'Bad Request',      'Missing required field, invalid amount, duplicate reference_id'],
                      [<span style={{ fontFamily:'IBM Plex Mono,monospace', color:'#b91c1c', fontWeight:700 }}>401</span>,'Unauthorized',     'Invalid API key, bad signature, expired timestamp (>5 min)'],
                      [<span style={{ fontFamily:'IBM Plex Mono,monospace', color:'#b91c1c', fontWeight:700 }}>403</span>,'Forbidden',        'IP not whitelisted, feature not enabled for your account'],
                      [<span style={{ fontFamily:'IBM Plex Mono,monospace', color:'#6a5a48', fontWeight:700 }}>404</span>,'Not Found',        'Order / payout number does not exist'],
                      [<span style={{ fontFamily:'IBM Plex Mono,monospace', color:'#b91c1c', fontWeight:700 }}>409</span>,'Conflict',         'Order already paid, duplicate payout request'],
                      [<span style={{ fontFamily:'IBM Plex Mono,monospace', color:'#9a5000', fontWeight:700 }}>422</span>,'Unprocessable',    'Deposit/payout system closed, amount below minimum'],
                      [<span style={{ fontFamily:'IBM Plex Mono,monospace', color:'#b91c1c', fontWeight:700 }}>429</span>,'Too Many Requests','Rate limit exceeded — back off and retry'],
                      [<span style={{ fontFamily:'IBM Plex Mono,monospace', color:'#991b1b', fontWeight:700 }}>500</span>,'Server Error',     'Internal error — contact support if persistent'],
                    ]} />
                  </div>

                  <div style={card}>
                    <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#1c1510', marginBottom:'0.75rem' }}>Slip Verification Errors (400)</div>
                    <ApiTable headers={['Message','Meaning']} rows={[
                      ['Duplicate slip',  'This slip has already been used to pay another order'],
                      ['Amount mismatch', 'Slip amount does not match the order total_amount'],
                      ['Wrong account',   'Transfer destination does not match the assigned bank account'],
                      ['Slip too old',    'Slip timestamp is outside the allowed window'],
                      ['Unreadable slip', 'Could not parse QR or image — retry with a clearer image'],
                    ]} />
                  </div>

                  <InfoCard type="tip" title="Rate Limits">
                    Default: <strong>300 requests / 15 minutes</strong> per IP. Headers{' '}
                    <code style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:'0.75rem' }}>X-RateLimit-Remaining</code> and{' '}
                    <code style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:'0.75rem' }}>X-RateLimit-Reset</code> are included in every response.
                  </InfoCard>
                </div>
              )}

              {/* ════ CODE EXAMPLES ═══════════════════════════════ */}
              {activeTab === 'examples' && (
                <div>
                  <SectionTitle icon={faCode}>Code Examples</SectionTitle>
                  <p style={{ ...muted, marginBottom:'1.5rem' }}>Complete integration examples using the V2 HMAC-signature API.</p>

                  <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1.5rem', flexWrap:'wrap' }}>
                    {(['javascript','php','python'] as const).map(lang => (
                      <button key={lang} className={`pdoc-lang-btn${selectedLang===lang?' pdoc-lang-active':''}`} onClick={() => setSelectedLang(lang)}>
                        <FontAwesomeIcon icon={LANG_ICONS[lang]} style={{ fontSize:'1rem' }} />
                        {lang.charAt(0).toUpperCase()+lang.slice(1)}
                      </button>
                    ))}
                  </div>

                  {[
                    { num:'1', title:'Create a Payment Order',      code: createOrderCode[selectedLang] },
                    { num:'2', title:'Handle Webhook (order.paid)', code: webhookCode[selectedLang]     },
                  ].map(({ num, title, code }) => (
                    <div key={num} style={{ marginBottom:'2rem' }}>
                      <div style={{ fontSize:'0.8rem', fontWeight:700, color:'#1c1510', marginBottom:'0.75rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
                        <span style={{ width:22, height:22, borderRadius:4, background:'rgba(181,101,10,0.10)', border:'1px solid rgba(181,101,10,0.22)', color:'#9a5000', fontSize:'0.65rem', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'IBM Plex Mono,monospace', fontWeight:700 }}>{num}</span>
                        {title}
                      </div>
                      <CodeBlock {...CB} id={`ex-${num}-${selectedLang}`} label={selectedLang} code={code} />
                    </div>
                  ))}

                  {/* CTA */}
                  <div className="pdoc-cta-glow" style={{ background:'rgba(181,101,10,0.06)', border:'1px solid rgba(181,101,10,0.22)', borderRadius:'1rem', padding:'2rem', textAlign:'center', marginTop:'2.5rem' }}>
                    <div style={{ width:48, height:48, borderRadius:'50%', background:'rgba(181,101,10,0.12)', border:'1px solid rgba(181,101,10,0.22)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1rem' }}>
                      <FontAwesomeIcon icon={faUserPlus} style={{ color:'#b5650a', fontSize:'1.25rem' }} />
                    </div>
                    <h3 style={{ fontSize:'1rem', fontWeight:700, color:'#1c1510', margin:'0 0 0.5rem', fontFamily:'IBM Plex Sans,sans-serif' }}>Ready to start accepting payments?</h3>
                    <p style={{ color:'#6a5a48', fontSize:'0.825rem', margin:'0 0 1.25rem', lineHeight:1.65 }}>
                      Create a merchant account and get your API Key & Secret in minutes.
                    </p>
                    <div style={{ display:'flex', gap:'0.75rem', justifyContent:'center', flexWrap:'wrap' }}>
                      <a href="/login" style={{ padding:'0.625rem 1.5rem', background:'linear-gradient(135deg,#9a4800,#c47012)', borderRadius:'0.5rem', color:'#fff', fontSize:'0.825rem', fontWeight:700, textDecoration:'none', fontFamily:'IBM Plex Mono,monospace', letterSpacing:'0.04em', boxShadow:'0 3px 12px rgba(181,101,10,0.28)' }}>
                        GET STARTED
                      </a>
                      <button onClick={() => setActiveTab('authentication')} style={{ padding:'0.625rem 1.5rem', background:'#ffffff', border:'1px solid #dfd5c5', borderRadius:'0.5rem', color:'#6a5a48', fontSize:'0.825rem', fontWeight:600, cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif' }}>
                        View Auth Guide
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default PublicApiDocsPage;
